import { call, put, select, takeEvery, delay, fork } from "redux-saga/effects";
import { AnyAction } from "redux";
import { IRootReducerState } from "../../rootReducer";
import { selectToken } from "../../selectors";
import { postAuditLogAPI } from "../../../axios/audit-logs/postAuditLog";
import { IAuditLogPayload } from "../../../types/audit";
import {
  savePendingEntries,
  removeFirstEntry,
  getPendingEntries,
} from "../../../helpers/audit/utils/auditQueue";
import {
  setAuditPrevMenuCategory,
  setAuditPrevMenuItem,
} from "../../actions/auditActions";
import {
  updateStoreWorkingHoursAction,
  updateBranchesWorkingHoursAction,
  createOrderingCategoryAction,
  editOrderingCategories,
  deleteOrderingMenuCategoryAction,
  createOrderingItemAction,
  editOrderingItemAction,
  deleteOrderingMenuItemAction,
  createOrderingModifiersGroupsAction,
  createOrderingSuggestedModifiersGroupsAction,
  editOrderingSuggestedModifiersGroupAction,
  deleteOrderingSuggestedModifiersGroupAction,
} from "../../../Components/Pickup/constants/actions";
import {
  deleteBranchAction,
  addCategoryAction,
  editCategoryAction,
  deleteCategoryAction,
  addStoreItemAction,
  editStoreItemAction,
  deleteStoreItemAction,
} from "../../../constants";
import { buildStoreWorkingHoursEntries } from "../../../helpers/audit/handlers/storeWorkingHours";
import { buildBranchesWorkingHoursEntries } from "../../../helpers/audit/handlers/branchesWorkingHours";
import { buildDeleteBranchEntries } from "../../../helpers/audit/handlers/deleteBranch";
import { buildMenuCategoryEntries } from "../../../helpers/audit/handlers/menuCategory";
import { buildMenuItemEntries } from "../../../helpers/audit/handlers/menuItem";
import { buildMenuModifierEntries } from "../../../helpers/audit/handlers/menuModifier";
import { buildWizardMenuCategoryEntries } from "../../../helpers/audit/handlers/wizardMenuCategory";
import { buildWizardMenuItemsEntries } from "../../../helpers/audit/handlers/wizardMenuItem";

type AuditEntryBuilder = (
  action: AnyAction,
  state: IRootReducerState
) => IAuditLogPayload[];

function* drainQueue(token: string) {
  let count = 0;
  while (true) {
    const pending: IAuditLogPayload[] = yield call(getPendingEntries);
    if (pending.length === 0) {
      break;
    }
    const entry = pending[0];
    yield call(removeFirstEntry);
    yield call(postAuditLogAPI, token, entry);
    count++;
  }
}

function* postAudit(entries: IAuditLogPayload[]) {
  const token: string = yield select(selectToken);
  if (!token) {
    return;
  }

  if (entries.length > 0) {
    yield call(savePendingEntries, entries);
  }

  yield call(drainQueue, token);
}

function* flushPendingAuditLogs() {
  while (true) {
    yield delay(30000);
    const token: string = yield select(selectToken);
    if (token) {
      yield call(drainQueue, token);
    }
  }
}

function* handleAction(action: AnyAction, builder: AuditEntryBuilder) {
  const state: IRootReducerState = yield select();
  const entries: IAuditLogPayload[] = yield call(builder, action, state);
  if (entries.length === 0) {
    return;
  }
  yield call(postAudit, entries);
}

function* handleStoreWorkingHours(action: AnyAction) {
  yield call(handleAction, action, buildStoreWorkingHoursEntries);
}

function* handleBranchesWorkingHours(action: AnyAction) {
  yield call(handleAction, action, buildBranchesWorkingHoursEntries);
}

function* handleDeleteBranch(action: AnyAction) {
  yield call(handleAction, action, buildDeleteBranchEntries);
}

function* handleMenuCategory(action: AnyAction) {
  yield call(handleAction, action, buildMenuCategoryEntries);
  yield put(setAuditPrevMenuCategory(null));
}

function* handleMenuItem(action: AnyAction) {
  yield call(handleAction, action, buildMenuItemEntries);
  yield put(setAuditPrevMenuItem(null));
}

function* handleMenuModifier(action: AnyAction) {
  yield call(handleAction, action, buildMenuModifierEntries);
}

function* handleWizardMenuCategory(action: AnyAction) {
  yield call(handleAction, action, buildWizardMenuCategoryEntries);
}

function* handleWizardMenuItem(action: AnyAction) {
  yield call(handleAction, action, buildWizardMenuItemsEntries);
}

export function* watchAuditMiddlewareSaga() {
  const token: string = yield select(selectToken);
  if (token) {
    yield fork(drainQueue, token);
  }
  yield fork(flushPendingAuditLogs);

  yield takeEvery(
    updateStoreWorkingHoursAction.fulfilled,
    handleStoreWorkingHours
  );
  yield takeEvery(
    updateBranchesWorkingHoursAction.fulfilled,
    handleBranchesWorkingHours
  );
  yield takeEvery(deleteBranchAction.fulfilled, handleDeleteBranch);

  yield takeEvery(createOrderingCategoryAction.fulfilled, handleMenuCategory);
  yield takeEvery(editOrderingCategories.fulfilled, handleMenuCategory);
  yield takeEvery(deleteOrderingMenuCategoryAction.fulfilled, handleMenuCategory);

  yield takeEvery(createOrderingItemAction.fulfilled, handleMenuItem);
  yield takeEvery(editOrderingItemAction.fulfilled, handleMenuItem);
  yield takeEvery(deleteOrderingMenuItemAction.fulfilled, handleMenuItem);

  yield takeEvery(createOrderingModifiersGroupsAction.fulfilled, handleMenuModifier);
  yield takeEvery(createOrderingSuggestedModifiersGroupsAction.fulfilled, handleMenuModifier);
  yield takeEvery(editOrderingSuggestedModifiersGroupAction.fulfilled, handleMenuModifier);
  yield takeEvery(deleteOrderingSuggestedModifiersGroupAction.fulfilled, handleMenuModifier);

  yield takeEvery(addCategoryAction.fulfilled, handleWizardMenuCategory);
  yield takeEvery(editCategoryAction.fulfilled, handleWizardMenuCategory);
  yield takeEvery(deleteCategoryAction.fulfilled, handleWizardMenuCategory);

  yield takeEvery(addStoreItemAction.fulfilled, handleWizardMenuItem);
  yield takeEvery(editStoreItemAction.fulfilled, handleWizardMenuItem);
  yield takeEvery(deleteStoreItemAction.fulfilled, handleWizardMenuItem);
}
